import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  Table,
  Group,
  Text,
  Badge,
  Select,
  Stack,
  Loader,
  Center,
  Alert,
  Pagination,
  Paper,
  Modal,
  Button,
  Textarea,
  ThemeIcon,
  Divider,
  Anchor,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
} from '@tabler/icons-react';
import { adminService } from '../../services/admin';
import { ReportStatus, ReportType, ReportReason } from '../../types';
import type { Report, ResolveReportRequest } from '../../types';
import { format } from 'date-fns';

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: '0', label: 'Pending' },
  { value: '1', label: 'Reviewed' },
  { value: '2', label: 'Action Taken' },
  { value: '3', label: 'Dismissed' },
];

const typeOptions = [
  { value: '', label: 'All types' },
  { value: '0', label: 'Review' },
  { value: '1', label: 'Comment' },
  { value: '2', label: 'User' },
];

const STATUS_COLOR: Record<number, string> = {
  [ReportStatus.Pending]: 'red',
  [ReportStatus.Reviewed]: 'blue',
  [ReportStatus.ActionTaken]: 'orange',
  [ReportStatus.Dismissed]: 'gray',
};

const STATUS_LABEL: Record<number, string> = {
  [ReportStatus.Pending]: 'Pending',
  [ReportStatus.Reviewed]: 'Reviewed',
  [ReportStatus.ActionTaken]: 'Action Taken',
  [ReportStatus.Dismissed]: 'Dismissed',
};

const TYPE_COLOR: Record<number, string> = {
  [ReportType.Review]: 'violet',
  [ReportType.Comment]: 'cyan',
  [ReportType.User]: 'pink',
};

const TYPE_LABEL: Record<number, string> = {
  [ReportType.Review]: 'Review',
  [ReportType.Comment]: 'Comment',
  [ReportType.User]: 'User',
};

const REASON_LABEL: Record<number, string> = {
  [ReportReason.Spam]: 'Spam',
  [ReportReason.Harassment]: 'Harassment',
  [ReportReason.HateSpeech]: 'Hate Speech',
  [ReportReason.Inappropriate]: 'Inappropriate',
  [ReportReason.FalseInformation]: 'False Information',
  [ReportReason.Other]: 'Other',
};

function getContentLink(report: Report): string | null {
  if (report.type === ReportType.Review) return `/reviews/${report.targetId}`;
  if (report.type === ReportType.User && report.targetUsername) return `/users/${report.targetUsername}`;
  return null;
}

function getTargetPreview(report: Report): string {
  if (report.targetTitle) return report.targetTitle;
  if (report.targetUsername) return `@${report.targetUsername}`;
  if (report.targetContent) {
    return report.targetContent.length > 60
      ? report.targetContent.substring(0, 60) + '…'
      : report.targetContent;
  }
  return 'Content unavailable';
}

// ── Single unified review modal ────────────────────────────────────────────
interface ReviewModalProps {
  report: Report | null;
  onClose: () => void;
  onDismiss: (report: Report, notes: string) => void;
  onRemoveContent: (report: Report, notes: string) => void;
  onMarkResolved: (report: Report, notes: string) => void;
  isPending: boolean;
}

function ReviewModal({ report, onClose, onDismiss, onRemoveContent, onMarkResolved, isPending }: ReviewModalProps) {
  const [notes, setNotes] = useState('');

  if (!report) return null;

  const isPendingReport = report.status === ReportStatus.Pending;
  const contentLink = getContentLink(report);
  const isUserReport = report.type === ReportType.User;

  return (
    <Modal
      opened={!!report}
      onClose={onClose}
      title="Report Details"
      size="lg"
    >
      <Stack gap="lg">
        {/* Status + type badges */}
        <Group gap="xs">
          <Badge color={STATUS_COLOR[report.status]} variant="filled" size="sm">
            {STATUS_LABEL[report.status]}
          </Badge>
          <Badge color={TYPE_COLOR[report.type]} variant="light" size="sm">
            {TYPE_LABEL[report.type]}
          </Badge>
          <Badge variant="light" color="gray" size="sm">
            {REASON_LABEL[report.reason]}
          </Badge>
        </Group>

        {/* Reporter + date */}
        <Group gap="xl">
          <div>
            <Text size="xs" c="dimmed" mb={2}>Reported by</Text>
            <Text size="sm" fw={500}>@{report.reporter.username}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" mb={2}>Date</Text>
            <Text size="sm">{format(new Date(report.createdAt), 'MMM d, yyyy · h:mm a')}</Text>
          </div>
        </Group>

        {/* Reporter's description */}
        {report.description && (
          <div>
            <Text size="xs" c="dimmed" mb={4}>Reporter's note</Text>
            <Text size="sm" fs="italic" c="dimmed">"{report.description}"</Text>
          </div>
        )}

        <Divider />

        {/* Reported content */}
        <div>
          <Group justify="space-between" mb="xs">
            <Text size="sm" fw={600}>Reported content</Text>
            {contentLink && (
              <Anchor component={Link} to={contentLink} size="xs" onClick={onClose}>
                View in context ↗
              </Anchor>
            )}
          </Group>

          {report.targetUsername && (
            <Text size="sm" fw={500} mb={6}>@{report.targetUsername}</Text>
          )}

          <Paper p="sm" radius="sm" withBorder style={{ maxHeight: 200, overflowY: 'auto' }}>
            {report.targetContent ? (
              <Text size="sm">{report.targetContent}</Text>
            ) : report.targetTitle ? (
              <Text size="sm">{report.targetTitle}</Text>
            ) : !report.targetUsername ? (
              <Text size="sm" c="dimmed" fs="italic">Content no longer available</Text>
            ) : null}
          </Paper>
        </div>

        {/* Previous moderation info */}
        {report.reviewedBy && (
          <div>
            <Text size="xs" c="dimmed" mb={4}>Handled by</Text>
            <Text size="sm">
              @{report.reviewedBy.username}
              {report.reviewedAt && ` · ${format(new Date(report.reviewedAt), 'MMM d, yyyy')}`}
            </Text>
            {report.moderatorNotes && (
              <Text size="sm" c="dimmed" mt={4} fs="italic">"{report.moderatorNotes}"</Text>
            )}
          </div>
        )}

        {/* Actions for pending reports */}
        {isPendingReport && (
          <>
            <Divider />
            <Textarea
              label="Moderator note (optional)"
              placeholder="Saved with this report for audit purposes..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              minRows={2}
              maxLength={1000}
            />

            <Group justify="space-between">
              <Button variant="default" onClick={onClose}>Cancel</Button>
              <Group gap="sm">
                <Button
                  variant="default"
                  onClick={() => { onDismiss(report, notes); setNotes(''); }}
                  loading={isPending}
                >
                  Dismiss
                </Button>
                {isUserReport ? (
                  <Button
                    color="orange"
                    onClick={() => { onMarkResolved(report, notes); setNotes(''); }}
                    loading={isPending}
                  >
                    Mark Resolved
                  </Button>
                ) : (
                  <Button
                    color="red"
                    onClick={() => { onRemoveContent(report, notes); setNotes(''); }}
                    loading={isPending}
                  >
                    Remove Content
                  </Button>
                )}
              </Group>
            </Group>
          </>
        )}

        {!isPendingReport && (
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Close</Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function Moderation() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('0'); // default to Pending
  const [typeFilter, setTypeFilter] = useState('');
  const [activeReport, setActiveReport] = useState<Report | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', page, statusFilter, typeFilter],
    queryFn: () =>
      adminService.getReports(
        page,
        20,
        statusFilter ? (Number(statusFilter) as never) : undefined,
        typeFilter ? (Number(typeFilter) as never) : undefined
      ),
  });

  const resolveMutation = useMutation({
    mutationFn: ({ id, request }: { id: string; request: ResolveReportRequest }) =>
      adminService.resolveReport(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      notifications.show({ title: 'Report resolved', message: 'Report has been updated', color: 'green', icon: <IconCheck size={16} /> });
      setActiveReport(null);
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to update report', color: 'red' });
    },
  });

  const deleteContentMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteReportedContent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      notifications.show({ title: 'Content removed', message: 'The reported content has been deleted', color: 'green', icon: <IconCheck size={16} /> });
      setActiveReport(null);
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to remove content', color: 'red' });
    },
  });

  const handleDismiss = (report: Report, notes: string) => {
    resolveMutation.mutate({
      id: report.id,
      request: { status: ReportStatus.Dismissed, moderatorNotes: notes || undefined },
    });
  };

  const handleRemoveContent = (report: Report, _notes: string) => {
    deleteContentMutation.mutate(report.id);
  };

  const handleMarkResolved = (report: Report, notes: string) => {
    resolveMutation.mutate({
      id: report.id,
      request: { status: ReportStatus.ActionTaken, moderatorNotes: notes || undefined },
    });
  };

  const pendingCount = data?.items.filter((r) => r.status === ReportStatus.Pending).length ?? 0;
  const isMutating = resolveMutation.isPending || deleteContentMutation.isPending;

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        Failed to load reports. Please try again later.
      </Alert>
    );
  }

  const rows = data?.items.map((report: Report) => (
    <Table.Tr
      key={report.id}
      onClick={() => setActiveReport(report)}
      style={{ cursor: 'pointer' }}
    >
      <Table.Td>
        <Badge color={TYPE_COLOR[report.type]} variant="light" size="sm">
          {TYPE_LABEL[report.type]}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{REASON_LABEL[report.reason]}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">@{report.reporter.username}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" lineClamp={1} maw={240} c={!report.targetContent && !report.targetTitle && !report.targetUsername ? 'dimmed' : undefined}>
          {getTargetPreview(report)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge color={STATUS_COLOR[report.status]} variant="light" size="sm">
          {STATUS_LABEL[report.status]}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {format(new Date(report.createdAt), 'MMM d, yyyy')}
        </Text>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Group gap="sm" align="center">
          <Title order={2}>Content Moderation</Title>
          {!statusFilter && pendingCount > 0 && (
            <Badge color="red" size="lg" circle>{pendingCount}</Badge>
          )}
        </Group>

        <Group gap="sm">
          <Select
            placeholder="Status"
            data={statusOptions}
            value={statusFilter}
            onChange={(value) => { setStatusFilter(value || ''); setPage(1); }}
            clearable
            w={160}
            size="sm"
          />
          <Select
            placeholder="Type"
            data={typeOptions}
            value={typeFilter}
            onChange={(value) => { setTypeFilter(value || ''); setPage(1); }}
            clearable
            w={140}
            size="sm"
          />
        </Group>
      </Group>

      <Paper shadow="xs" radius="md" withBorder>
        {isLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : data?.items.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon size={48} radius="xl" variant="light" color="green">
                <IconCheck size={24} />
              </ThemeIcon>
              <Text c="dimmed">
                {statusFilter === '0' ? 'No pending reports — queue is clear.' : 'No reports found.'}
              </Text>
            </Stack>
          </Center>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Type</Table.Th>
                <Table.Th>Reason</Table.Th>
                <Table.Th>Reporter</Table.Th>
                <Table.Th>Content</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Date</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        )}
      </Paper>

      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {data?.totalCount ? `${data.items.length} of ${data.totalCount} reports` : ''}
        </Text>
        <Pagination value={page} onChange={setPage} total={data?.totalPages || 1} />
      </Group>

      <ReviewModal
        report={activeReport}
        onClose={() => setActiveReport(null)}
        onDismiss={handleDismiss}
        onRemoveContent={handleRemoveContent}
        onMarkResolved={handleMarkResolved}
        isPending={isMutating}
      />
    </Stack>
  );
}
